import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { HardHat, Loader2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatApiError } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/sign-in'), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10 bg-[#0a0a0c] grid-bg">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-lg bg-[#ea9a6f] flex items-center justify-center">
            <HardHat className="w-5 h-5 text-[#0a0a0c]" strokeWidth={2.5} />
          </div>
          <span className="serif text-3xl text-white">Crew<span className="text-[#ea9a6f]">Face</span></span>
        </Link>

        <div className="bg-[#101014] border border-[#1e1e26] rounded-2xl p-8">
          {done ? (
            <div className="text-center py-4" data-testid="reset-success-panel">
              <div className="w-12 h-12 rounded-full bg-[#7dd4c5]/15 flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6 text-[#7dd4c5]" />
              </div>
              <h1 className="serif text-2xl text-white mb-1">Password updated</h1>
              <p className="text-sm text-[#8a877f]">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h1 className="serif text-3xl text-white mb-1">Set a new password</h1>
              <p className="text-sm text-[#8a877f] mb-6">Choose a new password for your account.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-[#c9c6bf] text-sm">New password</Label>
                  <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="reset-password-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
                </div>
                <div>
                  <Label className="text-[#c9c6bf] text-sm">Confirm password</Label>
                  <Input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
                </div>
                {error && <p className="text-sm text-red-400" data-testid="reset-error-message">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="reset-submit-button" className="w-full bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] font-medium rounded-full h-11">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</> : 'Update password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
